import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, SelectPicker, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import { getCitiesLookup } from '@/services/city.service';
import TablePagination from '@/components/TablePagination';
import { toastErrorMessage } from '@/utils/toasterMessage';
import ImageCell from '@/components/ImageCell';
import {
  RunningClubPaginationFilterRequest,
  ClubVisibilityList,
  ClubStatusList,
  ClubJoinTypeDisplayList,
  RunningClubList,
} from '@/models/running-club.model';
import { getPagedRunningClubs } from '@/services/running-club.service';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const RunningClubListPage = () => {

  const [filterReq, setFilterReq] = useState<RunningClubPaginationFilterRequest>(new RunningClubPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<RunningClubList[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: RunningClubPaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<RunningClubList> = await getPagedRunningClubs(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Running Clubs:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/running-clubs/new`)}>
          Add Running Club
        </Button>
        <Stack spacing={6}>
          <FetchingSelect
            label="City"
            fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
            value={filterReq.cityId}
            onChange={dataKey => setFilterReq(pre => ({...pre, cityId: dataKey}))}
          />               
          <Input
            placeholder="Keyword"
            value={filterReq.keyword ?? ''}
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
          <SelectPicker
            data={ClubStatusList}
            placeholder="Status"
            searchable={false}
            style={{ width: 140 }}
            onChange={value => setFilterReq(pre => ({...pre, status: value ?? undefined}))}
          />
          <SelectPicker
            data={ClubVisibilityList}
            placeholder="Visibility"
            searchable={false}
            style={{ width: 140 }}
            onChange={value => setFilterReq(pre => ({...pre, visibility: value ?? undefined}))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Logo</HeaderCell>
          <ImageCell dataKey="logoUrl" />
        </Column>
        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name"/>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>City</HeaderCell>
          <Cell dataKey="city.name" />
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Priority</HeaderCell>
          <Cell dataKey="priority" />
        </Column>
        <Column flexGrow={1} align="center">
          <HeaderCell>Visibility</HeaderCell>
          <Cell>
            {(row: RunningClubList) =>
              ClubVisibilityList.find(v => v.value === row.visibility)?.label ?? row.visibility}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center">
          <HeaderCell>Status</HeaderCell>
          <Cell>
            {(row: RunningClubList) =>
              ClubStatusList.find(s => s.value === row.status)?.label ?? row.status}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center">
          <HeaderCell>Join type</HeaderCell>
          <Cell>
            {(row: RunningClubList) =>
              ClubJoinTypeDisplayList.find(j => j.value === row.joinType)?.label ?? row.joinType}
          </Cell>
        </Column>
        <Column flexGrow={1} fixed="right" >
        <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (         
              <IconButton onClick={() => nav(`/running-clubs/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />  
            )}
          </Cell>
        </Column>
        
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default RunningClubListPage;
